import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import FluidReveal, { DebugMode, FluidRevealSettings, FxState, HeadFont, PortraitRect } from "@/components/hero-lab/FluidReveal";
import AudioFX, { AudioFXSettings } from "@/components/hero-lab/AudioFX";
import realAsset from "@/assets/damo-real.png.asset.json";
import maskAsset from "@/assets/damo-mask.png.asset.json";
import robotAsset from "@/assets/damo-robot-blue.png.asset.json";
// Final cyborg art (registered to the real portrait, clipped by the silhouette mask)
const realImg = realAsset.url;
const maskImg = maskAsset.url;
const ROBOT_SRC = robotAsset.url;
const ROBOT_PLACEHOLDER = false;
const robotImg = ROBOT_SRC;
import logo from "@/assets/damian-keyes-logo.png";
import { IntroOverlay } from "@/components/hero-lab/IntroOverlay";
import logosBar from "@/assets/logos-bar.png";

// Portrait source canvas. Swap to 3000/2400 when final assets arrive.
const ASPECT = 1920 / 1536; // 5:4, same as the 3000x2400 masters
// Visible subject bounds inside the canvas (measured from the mask)
const SUBJECT = { left: 0.18, right: 0.84, top: 0.044 };

// Editable copy
const COPY = {
  line1: ["MUSIC IS", "HUMAN."],
  line2: ["GROWTH IS", "ENGINEERED."],
  support: "The music comes from you. I help you build the audience around it.",
  cta: "EXPLORE THE SYSTEM",
  hint: "MOVE TO REVEAL",
};
const NAV = [
  { label: "About", href: "/#about" },
  { label: "Free Stuff", href: "/#free-stuff" },
];

const HEAD_FONTS: Record<string, HeadFont & { label: string }> = {
  geist: { label: "Geist (current)", family: "Geist, sans-serif", weight: 600, tracking: -0.045, scale: 1 },
  wide: { label: "Unbounded (wide)", family: "Unbounded, sans-serif", weight: 700, tracking: -0.03, scale: 0.78 },
  condensed: { label: "Anton (condensed)", family: "Anton, sans-serif", weight: 400, tracking: 0, scale: 1.25 },
  serif: { label: "Instrument Serif", family: "'Instrument Serif', serif", weight: 400, tracking: -0.02, scale: 1.2 },
};

const usePortraitRect = (): [PortraitRect, { w: number; h: number }] => {
  // Phones: size against the small viewport (100svh, same as the stage) so the iOS toolbar showing/hiding
  // on scroll never resizes or shifts the portrait into the headline. Desktop keeps innerHeight.
  const measure = () => {
    if (typeof window === "undefined") return { w: 1280, h: 800 };
    const w = window.innerWidth;
    if (w >= 768) return { w, h: window.innerHeight };
    const probe = document.createElement("div");
    probe.style.cssText = "position:fixed;top:0;left:0;width:0;height:100svh;visibility:hidden;pointer-events:none";
    document.body.appendChild(probe);
    const h = probe.offsetHeight || window.innerHeight;
    probe.remove();
    return { w, h };
  };
  const [vp, setVp] = useState(measure);
  useEffect(() => {
    const on = () => {
      const n = measure();
      setVp((p) => (p.w === n.w && p.h === n.h ? p : n));
    };
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const mobile = vp.w < 768;
  const sw = SUBJECT.right - SUBJECT.left;
  // size so the visible body fills the target height
  let h = (vp.h * (mobile ? 0.75 : 1.0)) / (1 - SUBJECT.top);
  let w = h * ASPECT;
  const maxSubjectW = vp.w * (mobile ? 0.98 : 0.67);
  if (w * sw > maxSubjectW) { w = maxSubjectW / sw; h = w / ASPECT; }
  const cx = vp.w / 2 - (SUBJECT.left + sw / 2 - 0.5) * w; // centre the body, not the canvas
  // Desktop: sink the body so the lower torso bleeds off the bottom edge and the head sits clear of the nav
  const drop = mobile ? 0 : 0.07;
  let y = vp.h - h * (0.95 - drop);
  if (!mobile) y = Math.max(y, vp.h * 0.14 - SUBJECT.top * h);
  y = Math.min(y, vp.h - h * 0.8); // never hide more than ~20% of the image
  return [{ x: cx - w / 2, y, w, h }, vp];
};

const APPLY_URL = "https://a23j5fry30t.typeform.com/to/CzjaFT5z";

const CONSULT_NAV = [
  { label: "About", href: "#founder" },
  { label: "Expertise", href: "#expertise" },
  { label: "Contact", href: "#contact" },
];

const HeroLab = ({ embedded = false, variant = "legacy" }: { embedded?: boolean; variant?: "legacy" | "consult" }) => {
  const [params] = useSearchParams();
  const dev = params.get("dev") === "1";
  const HeadTag = (variant === "consult" ? "p" : "h1") as "h1";
  const reduced = useMemo(() => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches, []);
  // WebGL unavailable or failed: show the same static portrait + DOM headline used for reduced motion
  const [glFailed, setGlFailed] = useState(false);
  const staticScene = reduced || glFailed;
  const lowPower = useMemo(() => typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches, []);
  const [rect, vp] = usePortraitRect();
  const fx = useRef<FxState>({ x: 0, y: 0, v: 0, active: false, parX: 0, parY: 0, full: 0 });
  const mainRef = useRef<HTMLElement>(null);
  const [ready, setReady] = useState(false);
  const onReady = useCallback(() => setReady(true), []);
  // Safety net: if the reveal never reports ready (e.g. a silent GPU/image failure on iOS), show the static portrait.
  useEffect(() => {
    if (ready || glFailed || reduced) return;
    const id = window.setTimeout(() => setGlFailed(true), 4000);
    return () => window.clearTimeout(id);
  }, [ready, glFailed, reduced]);
  const [open, setOpen] = useState(true);
  const [full, setFull] = useState(false);
  const [reducedReveal, setReducedReveal] = useState(false);

  const [s, setS] = useState<FluidRevealSettings>({
    size: 2.57, yaw: 0.15, pitch: 0.15, pivotEase: 0.075, seamJag: 0.28, seamGlow: 0.31, gStreams: 0.8, gSpeed: 1, gCurve: 0, strength: 1.3, decay: 0.93, viscosity: 0.15, velocity: 1, waves: 0.35, cap: 1, freq: 90, pinch: 0.15, glass: true, refraction: 0.25, split: 0.6, scan: 1.6, trailWaves: 0.8, bgPulse: 0.6, edgeAmp: 0.18, edgeMove: 2, dSpec: 1, dRib: 1, dVinyl: 1, dBloom: 1, dDust: 1,
    shadow: 1, grain: 1, vignette: 1, breath: 1, light: 1, soft: 1, thump: 1, quiet: 1, chest: 0,
    envBlend: 0, envReact: 1.5, xray: 0.42, xrayRate: 0.14, parallax: 1, fullReveal: 0, debug: "normal", mix: 0.5, showEnv: true,
  });
  const [a, setA] = useState<AudioFXSettings>({ wave: 0.35, pulse: 1, trail: false, ripples: true, drivers: true, circuits: true, rings: true });
  const [showAlign, setShowAlign] = useState(false);
  const [font, setFont] = useState("geist");
  const [underline, setUnderline] = useState(false);
  const [scrub, setScrub] = useState(-1);
  const [holdLen, setHoldLen] = useState(1);
  const [faceOffset, setFaceOffset] = useState(0.51);
  const [hud, setHud] = useState(true);
  const [bands, setBands] = useState(24);
  const [stagger, setStagger] = useState(0.9);
  const uiRef = useRef<HTMLDivElement>(null);
  const set = <K extends keyof FluidRevealSettings>(k: K, v: FluidRevealSettings[K]) => setS((p) => ({ ...p, [k]: v }));

  useEffect(() => { if (!embedded) document.title = "Hero Lab | Damian Keyes"; }, [embedded]);

  // Intro: always plays; exits after its minimum time once the scene is ready (3.5s fallback). Shorter on same-session revisits.
  const [introDone, setIntroDone] = useState(() => !embedded);
  const [introShort] = useState(() => typeof sessionStorage !== "undefined" && sessionStorage.getItem("dk-intro") === "1");
  const [introFallback, setIntroFallback] = useState(false);
  useEffect(() => {
    if (!embedded) return;
    const id = window.setTimeout(() => setIntroFallback(true), 3500);
    return () => window.clearTimeout(id);
  }, [embedded]);
  const finishIntro = () => { setIntroDone(true); sessionStorage.setItem("dk-intro", "1"); };

  // Exit "The Split": a jagged seam cuts human | machine, holds on the face, sweeps to full machine, then a scan wipe hands over
  const wrapRef = useRef<HTMLDivElement>(null);
  const washRef = useRef<HTMLDivElement>(null);
  const hudRef = useRef<HTMLDivElement>(null);
  const [hintOff, setHintOff] = useState(false);
  const hintOffRef = useRef(false);
  const hudTxt = useRef<Record<string, HTMLElement | null>>({});
  const logosRef = useRef<HTMLDivElement>(null);
  const bandsRef = useRef<HTMLDivElement>(null);
  const bandLinesRef = useRef<HTMLDivElement>(null);
  const live = useRef<{ bands: number; stagger: number; scrub: number; hold: number; faceX: number; rect: PortraitRect; vw: number; exit: string }>({ bands: 24, stagger: 0.9, scrub: -1, hold: 1, faceX: 0.51, rect, vw: vp.w, exit: "slide" });
  live.current = { bands, stagger, scrub: scrub, hold: holdLen, faceX: faceOffset, rect, vw: vp.w, exit: s.exitStyle ?? "slide" };
  useEffect(() => {
    let raf = 0, sp = 0, frame = 0;
    const sm = (a: number, b: number, x: number) => { const k = Math.min(1, Math.max(0, (x - a) / (b - a))); return k * k * (3 - 2 * k); };
    const loop = () => {
      raf = requestAnimationFrame(loop); frame++;
      const L = live.current;
      let target = 0;
      if (embedded || L.scrub < 0) {
        const el = wrapRef.current; if (!el) return;
        const r = el.getBoundingClientRect();
        target = Math.min(1, Math.max(0, -r.top / Math.max(1, r.height - window.innerHeight)));
      } else target = L.scrub < 0 ? 0 : L.scrub;
      sp += (target - sp) * (reduced ? 1 : 0.12);
      const p = sp;
      // timeline: seam grows up from the bottom, holds split, then the next section rises over it
      const a0 = 0.02, a1 = 0.26, b1 = 0.34;
      const face = (L.rect.x + L.rect.w * L.faceX) / Math.max(1, L.vw);
      const grow = reduced ? (p > a0 ? 1 : 0) : sm(a0, a1, p);
      const seam = grow > 0.001 ? face : 1.2;
      fx.current.seam = seam;
      fx.current.seamY = grow * 1.06;
      const rise = sm(b1, 1, p);
      void rise;
      // /hero-lab preview of the rising page (the homepage uses the real next section)
      { const off = p > 0.02; if (off !== hintOffRef.current) { hintOffRef.current = off; setHintOff(off); } }
      // HUD
      const hudOn = sm(a0, a0 + 0.1, p) * (1 - sm(0.45, 0.65, p));
      if (hudRef.current) hudRef.current.style.opacity = String(hudOn);
      if (hudOn > 0.01 && frame % 3 === 0) {
        const T = hudTxt.current, f = fx.current;
        const st = p > b1 ? "ENGINEERED" : p > a1 ? "CONVERTING" : "SCANNING";
        if (T.yaw) T.yaw.textContent = `YAW ${(f.parX * 4).toFixed(1)}°  PITCH ${(-f.parY * 3).toFixed(1)}°`;
        if (T.status) T.status.textContent = st;
        if (T.bpm) T.bpm.textContent = `${(118 + Math.sin(performance.now() / 900) * 2).toFixed(1)} BPM`;
        if (T.bars) Array.from(T.bars.children).forEach((b, i) => {
          const v = 0.25 + 0.75 * Math.abs(Math.sin(performance.now() / (260 + i * 37) + i)) * (0.6 + (f.thump ?? 0) * 0.8);
          (b as HTMLElement).style.transform = `scaleY(${v.toFixed(2)})`;
        });
      }
      if (logosRef.current) { const lg = logosRef.current; lg.style.clipPath = `inset(0 0 0 ${(Math.max(0, Math.min(1, seam)) * 100).toFixed(2)}%)`; lg.style.opacity = String(sm(a1 - 0.02, a1 + 0.06, p) * (1 - sm(b1, b1 + 0.15, p))); }
      // exit: hero eases back and dims while the next section slides over it
      const ex = sm(b1, 0.95, p);
      const iris = L.exit === "iris";
      fx.current.iris = iris ? ex : 0;
      if (bandsRef.current && iris) { bandsRef.current.style.transform = ""; bandsRef.current.style.opacity = "1"; }
      else if (bandsRef.current) {
        bandsRef.current.style.transform = ex > 0.0005 ? `scale(${(1 - ex * 0.04).toFixed(4)})` : "";
        bandsRef.current.style.opacity = String(1 - ex * 0.35);
      }
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [embedded, reduced]);

  const goTo = (e: React.MouseEvent, href: string) => {
    const id = href.split("#")[1];
    const el = id && document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    const top = el.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    history.replaceState(null, "", `#${id}`);
  };

  // ease full transformation
  useEffect(() => {
    let raf = 0;
    const target = full ? 1 : 0;
    const step = () => setS((p) => {
      const v = p.fullReveal + (target - p.fullReveal) * 0.035;
      if (Math.abs(v - target) < 0.002) return { ...p, fullReveal: target };
      raf = requestAnimationFrame(step);
      return { ...p, fullReveal: v };
    });
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [full]);

  // DOM parallax: typography & nav drift at different strengths
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const el = mainRef.current; if (!el) return;
      if (window.innerWidth < 768) { el.style.setProperty("--px", "0"); el.style.setProperty("--py", "0"); return; } // phones: text stays put
      el.style.setProperty("--px", fx.current.parX.toFixed(4));
      el.style.setProperty("--py", fx.current.parY.toFixed(4));
    };
    if (!reduced) loop();
    return () => cancelAnimationFrame(raf);
  }, [reduced]);

  const mobile = vp.w < 768;
  const touchUI = mobile && typeof window !== "undefined" && window.matchMedia("(hover: none)").matches;
  const hintText = reduced ? "TAP TO REVEAL" : touchUI ? "SWIPE OR TAP TO REVEAL" : COPY.hint;
  const fxHide = staticScene ? "" : "hero-fx-hide";
  const layer = (depth: number) => ({ transform: `translate3d(calc(var(--px,0) * ${-depth}px), calc(var(--py,0) * ${depth}px), 0)` });

  const stage = (
    <main ref={mainRef} data-world={(staticScene ? reducedReveal : s.envBlend > 0.5) ? "machine" : "human"} data-accent={s.accent ?? "blue"} className="hero-lab relative h-[100svh] min-h-[100svh] w-full overflow-hidden bg-hero-human select-none">
      {/* Scene: environment + portrait composite */}
      {staticScene ? (
        <div className={`absolute inset-0 transition-colors duration-1000 ${reducedReveal ? "bg-hero-machine" : "bg-hero-human"} outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-hero-blue`} onClick={() => setReducedReveal((r) => !r)} role="button" tabIndex={0} aria-pressed={reducedReveal} aria-label={reducedReveal ? "Show the human portrait" : "Reveal the engineered machine portrait"} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setReducedReveal((r) => !r); } }}>
          <img src={realImg} alt="Damian Keyes" className="absolute" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }} />
          <img src={robotImg} alt="" className="absolute transition-opacity duration-1000" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h, opacity: reducedReveal ? 1 : 0 }} />
        </div>
      ) : (
        <>
          <FluidReveal realSrc={realImg} robotSrc={robotImg} maskSrc={maskImg} robotPlaceholder={ROBOT_PLACEHOLDER} aspect={ASPECT} rect={rect} settings={s} fx={fx} onReady={onReady} onFail={() => setGlFailed(true)} lowPower={lowPower} headline={{ left: COPY.line1, right: COPY.line2 }} headFont={HEAD_FONTS[font]} overlayRoot={uiRef} underline={underline} redrawKey={`${ready}-${vp.w}-${hintOff}`} />
          <AudioFX fx={fx} rect={rect} settings={a} />
        </>
      )}

      {showAlign && (
        <div className="pointer-events-none absolute border border-dashed border-hero-signal" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }} />
      )}

      {/* Interface layer */}
      <div ref={uiRef} className={`pointer-events-none absolute inset-0 transition-opacity duration-[1400ms] ${ready || staticScene ? "opacity-100" : "opacity-0"}`}>
        <header className="pointer-events-auto absolute inset-x-0 top-0 flex items-center justify-between px-6 py-5 md:px-12 md:py-7">
          <img data-fx data-intro-target src={logo} alt="Damian Keyes" className={`hero-logo -ml-2 h-11 w-auto md:-ml-[14px] md:h-[68px] ${fxHide}`} />
          <nav aria-label="Main" className="hero-ink flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.12em] md:gap-10 md:text-[12.5px] md:tracking-[0.16em]">
            {(variant === "consult" ? CONSULT_NAV : [...NAV, { label: "Apply", href: APPLY_URL }]).map((n, i, arr) => {
              const apply = i === arr.length - 1;
              const external = n.href.startsWith("http");
              return (
                <a key={n.label} href={n.href} onClick={external ? undefined : (e) => goTo(e, n.href)} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className={`${apply ? "relative flex px-3.5 py-2" : `hero-nav-link ${variant === "consult" ? (n.href === "#expertise" ? "hidden sm:flex" : "flex") : embedded ? "hidden sm:flex" : "hidden md:flex"}`} items-center gap-2`}>
                  {apply && <span data-fx-box aria-hidden className="absolute inset-0" />}
                  <span data-fx={`0${i + 1}`} className={`opacity-50 ${variant === "consult" ? "hidden sm:inline" : ""} ${fxHide}`}>0{i + 1}</span>
                  <span data-fx={n.label.toUpperCase()} className={fxHide}>{n.label}</span>
                </a>
              );
            })}
          </nav>
        </header>

        {/* Editorial headline framing the silhouette */}
        {!staticScene ? (
          <HeadTag className="sr-only">{[...COPY.line1, ...COPY.line2].join(" ")}</HeadTag>
        ) : mobile ? (
          <HeadTag className="hero-ink absolute inset-x-6 top-24 text-center text-[11vw] font-semibold leading-[0.9] tracking-[-0.04em]" style={layer(6)}>
            {[...COPY.line1, ...COPY.line2].map((l, i) => <span key={l} className={i === COPY.line1.length ? "mt-3.5 block" : "block"}>{l}</span>)}
          </HeadTag>
        ) : (
          <HeadTag className="hero-ink font-semibold leading-[0.88] tracking-[-0.045em]" style={{ fontSize: "clamp(2.5rem, 4.6vw, 6rem)" }}>
            <span className="absolute top-[34%] block" style={{ ...layer(8), left: Math.max(48, (vp.w - 1440) / 2) }}>
              {COPY.line1.map((l) => <span key={l} className="block">{l}</span>)}
            </span>
            <span className="absolute top-[34%] block text-right" style={{ ...layer(8), right: Math.max(48, (vp.w - 1440) / 2) }}>
              {COPY.line2.map((l) => <span key={l} className="block">{l}</span>)}
            </span>
          </HeadTag>
        )}


        <p data-fx-off={hintOff ? "" : undefined} style={{ opacity: hintOff ? 0 : 1 }} className={`absolute flex items-center font-mono uppercase md:bottom-12 md:right-12 ${touchUI ? "bottom-6 right-4 gap-2 border border-hero-machine/10 bg-hero-light/90 px-2.5 py-1.5 text-[10.5px] font-semibold tracking-[0.2em] text-hero-machine shadow-sm backdrop-blur-sm" : "hero-ink bottom-8 right-6 gap-3 text-[10px] tracking-[0.3em]"}`}>
          <span data-fx-line={touchUI ? undefined : ""} aria-hidden className={reduced && !touchUI ? "hero-hint-line" : touchUI ? "inline-block h-px w-5 bg-current" : "inline-block h-px w-10"} /><span data-fx={touchUI ? undefined : hintText} className={touchUI ? "" : fxHide}>{hintText}</span>
        </p>
      </div>

      {/* Technical HUD (only during the split) */}
      <div ref={hudRef} aria-hidden className={`pointer-events-none absolute inset-0 z-30 font-mono text-[10px] uppercase tracking-[0.18em] opacity-0 ${hud ? "" : "hidden"}`}>
        {["right-4 top-4 border-r border-t", "right-4 bottom-4 border-r border-b"].map((c) => (
          <span key={c} className={`absolute h-4 w-4 border-hero-blue/70 ${c}`} />
        ))}
        <div className="absolute bottom-8 right-6 flex flex-col items-end gap-2 text-right text-hero-light md:bottom-12 md:right-12">
          <div className="space-y-1">
            <p>FACE MESH <span className="opacity-60">478 PTS</span></p>
            <p ref={(el) => (hudTxt.current.yaw = el)} className="opacity-70">YAW 0.0°  PITCH 0.0°</p>
            <p className="flex items-center justify-end gap-2"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-hero-blue" /><span ref={(el) => (hudTxt.current.status = el)}>SCANNING</span></p>
          </div>
          <div ref={(el) => (hudTxt.current.bars = el)} className="flex h-6 items-end gap-[3px]">
            {Array.from({ length: 16 }, (_, i) => <span key={i} className="block h-full w-[3px] origin-bottom bg-hero-blue/80" />)}
          </div>
          <p ref={(el) => (hudTxt.current.bpm = el)}>118.0 BPM</p>
        </div>
      </div>

      {dev && !embedded && (
        <aside className="fixed left-4 top-20 z-50 max-h-[calc(100svh-6rem)] w-64 space-y-2 overflow-y-auto overscroll-contain rounded-md border-2 border-hero-signal bg-card/95 p-4 text-xs text-card-foreground shadow-lg backdrop-blur">
          <p className="font-semibold">Cursor & 3D pivot</p>
          <Slider label="Cursor blob size" v={s.size} min={0.4} max={4} on={(v) => set("size", v)} />
          <Slider label="Side-to-side pivot" v={s.yaw} min={0} max={3} on={(v) => set("yaw", v)} />
          <Slider label="Forward / back lean" v={s.pitch} min={0} max={3} on={(v) => set("pitch", v)} />
          <Slider label="Pivot smoothness" v={s.pivotEase} min={0.01} max={0.2} step={0.005} on={(v) => set("pivotEase", v)} />
          <p className="pt-2 font-semibold">Eyes & Chrome</p>
          <Slider label="Eye follow" v={s.eyeFollow ?? 1} min={0} max={2.5} on={(v) => set("eyeFollow", v)} />
          <Slider label="Eye glow" v={s.eyeGlow ?? 1} min={0} max={2.5} on={(v) => set("eyeGlow", v)} />
          <Slider label="Blink rate" v={s.blinkRate ?? 1} min={0.1} max={3} on={(v) => set("blinkRate", v)} />
          <Slider label="Chrome sweep" v={s.sweep ?? 1} min={0} max={2.5} on={(v) => set("sweep", v)} />
          <Slider label="Cursor reflection" v={s.reflect ?? 1} min={0} max={2.5} on={(v) => set("reflect", v)} />
          <Slider label="Headphone glow" v={s.earGlow ?? 1} min={0} max={2.5} on={(v) => set("earGlow", v)} />
          <Slider label="Mouth meter" v={s.mouthMeter ?? 1} min={0} max={2.5} on={(v) => set("mouthMeter", v)} />
          <p className="pt-2 font-semibold">Robot lines</p>
          <Slider label="Neon intensity" v={s.rlNeon ?? 1.4} min={0} max={3} on={(v) => set("rlNeon", v)} />
          <Slider label="Bloom size" v={s.rlBloom ?? 1} min={0} max={2.5} on={(v) => set("rlBloom", v)} />
          <Slider label="Surround contrast" v={s.rlContrast ?? 0.6} min={0} max={1.5} on={(v) => set("rlContrast", v)} />
          <p className="pt-2 font-semibold">Copper & speakers</p>
          <label className="flex items-center gap-2"><input type="checkbox" checked={s.copper !== false} onChange={(e) => set("copper", e.target.checked)} /> Copper heartbeat</label>
          <Slider label="Copper glow" v={s.cuAmt ?? 1} min={0} max={2.5} on={(v) => set("cuAmt", v)} />
          <Slider label="Copper pulse speed" v={s.cuSpeed ?? 1} min={0.2} max={3} on={(v) => set("cuSpeed", v)} />
          <Slider label="Blue-to-copper hand-off" v={s.cuHand ?? 0.8} min={0} max={2} on={(v) => set("cuHand", v)} />
          <Slider label="Speaker rings" v={s.spkAmt ?? 1} min={0} max={2.5} on={(v) => set("spkAmt", v)} />
          <Slider label="Speaker ring size" v={s.spkSize ?? 1} min={0.5} max={2} on={(v) => set("spkSize", v)} />
          <label className="flex items-center gap-2"><input type="checkbox" checked={s.robotLines !== false} onChange={(e) => set("robotLines", e.target.checked)} /> Living blue lines</label>
          <Slider label="Pulse speed" v={s.rlSpeed ?? 1} min={0.2} max={3} on={(v) => set("rlSpeed", v)} />
          <Slider label="Pulse brightness" v={s.rlPulse ?? 0.6} min={0} max={2} on={(v) => set("rlPulse", v)} />
          <Slider label="Breathing amount" v={s.rlBreath ?? 0.6} min={0} max={2} on={(v) => set("rlBreath", v)} />
          <Slider label="Cursor wake" v={s.rlWake ?? 0.6} min={0} max={2} on={(v) => set("rlWake", v)} />
          <Slider label="Colour range" v={s.rlRange ?? 1} min={0} max={1} on={(v) => set("rlRange", v)} />
          <Slider label="Glow spread" v={s.rlGlow ?? 1} min={0} max={2.5} on={(v) => set("rlGlow", v)} />
          <Slider label="Currents" v={s.rlCurrents ?? 3} min={1} max={3} step={1} on={(v) => set("rlCurrents", v)} />
          <p className="pt-2 font-semibold">Human side (locked split)</p>
          <Slider label="Ink in water" v={s.ink ?? 1} min={0} max={2.5} on={(v) => set("ink", v)} />
          <Slider label="Paper & lamp" v={s.lamp ?? 1} min={0} max={2.5} on={(v) => set("lamp", v)} />
          <Slider label="Pencil trace" v={s.pencil ?? 1} min={0} max={2.5} on={(v) => set("pencil", v)} />
          <Slider label="Warm dust" v={s.dust ?? 1} min={0} max={2.5} on={(v) => set("dust", v)} />
          <label className="flex items-center justify-between gap-2 pt-1"><span>Accent colour</span>
            <select className="rounded border bg-background px-1" value={s.accent ?? "blue"} onChange={(e) => set("accent", e.target.value as "blue" | "green" | "emerald")}>
              <option value="blue">Blue (current)</option><option value="green">Spotify green</option><option value="emerald">Deeper emerald</option>
            </select></label>
          <label className="flex items-center justify-between gap-2 pt-1"><span>Exit style</span>
            <select className="rounded border bg-background px-1" value={s.exitStyle ?? "slide"} onChange={(e) => set("exitStyle", e.target.value as "slide" | "iris")}>
              <option value="slide">Current (slide)</option><option value="iris">Iris close</option>
            </select></label>
          <Slider label="HUMAN letters breathe" v={s.humanBreathe ?? 1} min={0} max={2.5} on={(v) => set("humanBreathe", v)} />
          <p className="pt-2 font-semibold">Scroll split</p>
          <Slider label="Scrub (-1 = off)" v={scrub} min={-1} max={1} step={0.005} on={setScrub} />
          <Slider label="Seam jaggedness" v={s.seamJag ?? 1} min={0} max={3} on={(v) => set("seamJag", v)} />
          <Slider label="Seam glow" v={s.seamGlow ?? 1} min={0} max={3} on={(v) => set("seamGlow", v)} />
          <Toggle label="HUD" v={hud} on={setHud} />
          <p className="pt-2 font-semibold">Growth signals</p>
          <Slider label="Streams amount" v={s.gStreams ?? 1} min={0} max={2} on={(v) => set("gStreams", v)} />
          <Slider label="Stream speed" v={s.gSpeed ?? 1} min={0.2} max={3} on={(v) => set("gSpeed", v)} />
        </aside>
      )}
      {dev && !embedded && (
        <aside className="fixed right-4 top-20 z-50 max-h-[80vh] w-72 overflow-y-auto rounded-md border border-border bg-card/95 p-4 text-xs text-card-foreground shadow-lg backdrop-blur">
          <button className="mb-2 w-full text-left font-semibold uppercase tracking-wider" onClick={() => setOpen((o) => !o)}>Dev controls {open ? "−" : "+"}</button>
          {open && (
            <div className="space-y-3">
              <p className="rounded bg-muted p-2 text-muted-foreground">Robot = final cyborg art, clipped to the photo silhouette.</p>
              <button className="w-full rounded border border-input py-1.5 font-medium" onClick={() => setFull((f) => !f)}>{full ? "Back to human" : "Full transformation"}</button>
              <label className="block">View
                <select className="mt-1 w-full rounded border border-input bg-background p-1" value={s.debug} onChange={(e) => set("debug", e.target.value as DebugMode)}>
                  <option value="normal">Reveal</option>
                  <option value="opacity">Opacity compare</option>
                  <option value="split">Before / after split</option>
                  <option value="overlay">Mask alignment</option>
                  <option value="mask">Fluid debug</option>
                </select>
              </label>
              {(s.debug === "opacity" || s.debug === "split") && <Slider label="Mix" v={s.mix} min={0} max={1} on={(v) => set("mix", v)} />}
              <p className="pt-1 font-semibold">Look</p>
              <Select label="Headline font" v={font} on={setFont} opts={Object.entries(HEAD_FONTS).map(([k, f]) => [k, f.label])} />
              <Toggle label="Underline ENGINEERED" v={underline} on={setUnderline} />
              <p className="pt-1 font-semibold">Dark world layers</p>
              <Slider label="Waveform ribbons" v={s.dRib} min={0} max={2} on={(v) => set("dRib", v)} />
              <Slider label="Vinyl rings" v={s.dVinyl} min={0} max={2} on={(v) => set("dVinyl", v)} />
              <Slider label="Beat bloom" v={s.dBloom} min={0} max={2} on={(v) => set("dBloom", v)} />
              <Slider label="Neon dust" v={s.dDust} min={0} max={2} on={(v) => set("dDust", v)} />
              <p className="pt-1 font-semibold">Reveal</p>
              <p className="pt-2 opacity-60">Realism & bass</p>
              <Slider label="Contact shadow" v={s.shadow} min={0} max={2} on={(v) => set("shadow", v)} />
              <Slider label="Robot cursor light" v={s.light} min={0} max={2} on={(v) => set("light", v)} />
              <Slider label="Soft seam" v={s.soft} min={0} max={2} on={(v) => set("soft", v)} />
              <Slider label="Film grain" v={s.grain} min={0} max={2} on={(v) => set("grain", v)} />
              <Slider label="Vignette" v={s.vignette} min={0} max={2} on={(v) => set("vignette", v)} />
              <Slider label="Breathing" v={s.breath} min={0} max={3} on={(v) => set("breath", v)} />
              <Slider label="Bass thump" v={s.thump} min={0} max={2} on={(v) => set("thump", v)} />
              <Slider label="Quiet when resting" v={s.quiet} min={0} max={1} on={(v) => set("quiet", v)} />
              <Slider label="Chest sub-bass ripple" v={s.chest} min={0} max={2} on={(v) => set("chest", v)} />
              <Slider label="Ripple amount" v={s.waves} min={0} max={1} on={(v) => set("waves", v)} />
              <Slider label="Max robot visible" v={s.cap} min={0.2} max={1} on={(v) => set("cap", v)} />
              <Slider label="Reveal size" v={s.size} min={0.4} max={3} on={(v) => set("size", v)} />
              <Slider label="Strength" v={s.strength} min={0.2} max={3} on={(v) => set("strength", v)} />
              <Slider label="Wave edge detail" v={s.edgeAmp} min={0} max={0.5} on={(v) => set("edgeAmp", v)} />
              <Slider label="Wave edge movement" v={s.edgeMove} min={0} max={8} on={(v) => set("edgeMove", v)} />
              <Slider label="Trail length / decay" v={s.decay} min={0.88} max={0.995} step={0.001} on={(v) => set("decay", v)} />
              <Slider label="Viscosity (drag)" v={s.viscosity} min={0} max={2} on={(v) => set("viscosity", v)} />
              <Slider label="Velocity sensitivity" v={s.velocity} min={0.2} max={3} on={(v) => set("velocity", v)} />
              <p className="pt-1 font-semibold">Environment</p>
              <Slider label="Line X-ray strength" v={s.xray} min={0} max={1.5} on={(v) => set("xray", v)} />
              <Slider label="X-ray flow speed" v={s.xrayRate} min={0.02} max={0.4} on={(v) => set("xrayRate", v)} />
              <Slider label="Light / dark blend" v={s.envBlend} min={0} max={1} on={(v) => set("envBlend", v)} />
              <Slider label="Neon dark world (0 = off)" v={s.envReact} min={0} max={1.5} on={(v) => set("envReact", v)} />
              <Slider label="Parallax" v={s.parallax} min={0} max={3} on={(v) => set("parallax", v)} />
              <Toggle label="Text static effect" v={s.glass} on={(v) => set("glass", v)} />
              <Slider label="Static strength" v={s.refraction} min={0} max={1.5} on={(v) => set("refraction", v)} />
              <Slider label="Colour split" v={s.split} min={0} max={4} on={(v) => set("split", v)} />
              <Slider label="Scan line density" v={s.scan} min={0.3} max={4} on={(v) => set("scan", v)} />
              <Slider label="Cursor wave trail (0 = off)" v={s.trailWaves} min={0} max={2} on={(v) => set("trailWaves", v)} />
              <Slider label="Background pulse (0 = off)" v={s.bgPulse} min={0} max={2} on={(v) => set("bgPulse", v)} />
              <Toggle label="Environment" v={s.showEnv} on={(v) => set("showEnv", v)} />
              <Toggle label="Alignment box" v={showAlign} on={setShowAlign} />
              <p className="pt-1 font-semibold">Sound effects</p>
              <Slider label="Soundwave intensity" v={a.wave} min={0} max={2} on={(v) => setA((p) => ({ ...p, wave: v }))} />
              <Slider label="Circuit pulse intensity" v={a.pulse} min={0} max={2} on={(v) => setA((p) => ({ ...p, pulse: v }))} />
              {(["trail", "ripples", "drivers", "circuits", "rings"] as const).map((k) => (
                <Toggle key={k} label={k} v={a[k]} on={(v) => setA((p) => ({ ...p, [k]: v }))} />
              ))}
            </div>
          )}
        </aside>
      )}
    </main>
  );
  return (
    <>
    <div ref={wrapRef} className="relative z-10 h-[250svh]">
      <div className="sticky top-0 h-[100svh] overflow-hidden">
        <div ref={bandsRef} className="absolute inset-0 origin-top will-change-transform">{stage}</div>
        {/* soft light wash that hands over to the page below */}
        {/* intro: logo while the scene loads */}
        {embedded && !introDone && <IntroOverlay ready={ready || staticScene || introFallback} short={introShort} onDone={finishIntro} />}
      </div>
    </div>
    {!embedded && (
      <section id="about" style={{ marginTop: "-100svh" }} className="relative z-20 min-h-[100svh] rounded-t-[2rem] bg-hero-human px-6 py-32 text-center shadow-[0_-24px_60px_-20px_hsl(0_0%_0%/0.35)]">
        <p className="mb-6 font-mono text-xs tracking-[0.2em] text-muted-foreground">01 ABOUT (PREVIEW)</p>
        <h2 className="mx-auto max-w-5xl font-headline text-[clamp(2rem,4.5vw,3.5rem)] font-bold leading-[1.05] tracking-[-0.05em] text-[hsl(var(--light-text))]">
          Get your music heard, build a genuine fanbase and become an Entrepreneurial Musician
        </h2>
      </section>
    )}
    </>
  );
};

const Slider = ({ label, v, min, max, step = 0.01, on }: { label: string; v: number; min: number; max: number; step?: number; on: (v: number) => void }) => (
  <label className="block">{label} <span className="text-muted-foreground">{v.toFixed(step < 0.01 ? 3 : 2)}</span>
    <input type="range" min={min} max={max} step={step} value={v} onChange={(e) => on(+e.target.value)} className="w-full" />
  </label>
);
const Select = ({ label, v, on, opts }: { label: string; v: string; on: (v: string) => void; opts: string[][] }) => (
  <label className="block">{label}
    <select className="mt-1 w-full rounded border border-input bg-background p-1" value={v} onChange={(e) => on(e.target.value)}>
      {opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  </label>
);
const Toggle = ({ label, v, on }: { label: string; v: boolean; on: (v: boolean) => void }) => (
  <label className="flex items-center justify-between capitalize">{label}<input type="checkbox" checked={v} onChange={(e) => on(e.target.checked)} /></label>
);

export default HeroLab;
